import { customRef, type Ref } from 'vue';

import { z } from 'zod';

const localStorageSchemas = {
  interactiveAssignmentShowHintsAndAttempts: z.boolean(),
  // record of studentSessions keyed by code
  // temporary solution until we have proper student access
  studentSessions: z.record(z.string(), z.string()),
  studentNickname: z.string().nullable(),
  aiAge: z.string(),
  aiLanguage: z.string(),
  recentIpiPrompts: z.array(z.string()),
  recentImageLibraries: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
} as const;

type LocalStorageSchema = typeof localStorageSchemas;
type LocalStorageKey = keyof LocalStorageSchema;

let localStorageRefs: Partial<
  Record<LocalStorageKey, Ref<z.infer<LocalStorageSchema[LocalStorageKey]>>>
> = {};

/**
 * only used for unit tests
 */
export function reset() {
  localStorageRefs = {};
}

function setupLocalStorageRef<T extends LocalStorageKey>(
  key: T,
  value: z.infer<LocalStorageSchema[T]>,
): Ref<z.infer<LocalStorageSchema[T]>> {
  const ref = customRef<z.infer<LocalStorageSchema[T]>>((track, trigger) => {
    return {
      get() {
        track();
        return value;
      },
      set(newValue) {
        const serialized =
          typeof newValue === 'object'
            ? JSON.stringify(newValue)
            : String(newValue);
        localStorage.setItem(key, serialized);
        value = newValue;
        trigger();
      },
    };
  });
  ref.value = value;
  localStorageRefs[key] = ref as Ref<
    z.infer<LocalStorageSchema[LocalStorageKey]>
  >;
  return ref;
}

function unwrapJsonString(maybeObject: string): unknown {
  try {
    return JSON.parse(maybeObject);
  } catch {
    return maybeObject;
  }
}

export function useLocalStorage<T extends LocalStorageKey>(
  key: T,
  defaultValue: z.infer<LocalStorageSchema[T]>,
): Ref<z.infer<LocalStorageSchema[T]>> {
  if (localStorageRefs[key]) {
    return localStorageRefs[key] as Ref<z.infer<LocalStorageSchema[T]>>;
  }

  const storedValue = localStorage.getItem(key);
  const schema = localStorageSchemas[key];

  if (storedValue === null) {
    return setupLocalStorageRef(key, defaultValue);
  }

  const parsed = unwrapJsonString(storedValue);

  const validationResult = schema.safeParse(parsed);
  if (validationResult.success) {
    return setupLocalStorageRef(
      key,
      validationResult.data as z.infer<LocalStorageSchema[T]>,
    );
  } else {
    console.warn(
      `Failed to parse value "${JSON.stringify(parsed)}" for key "${key}", falling back to "${JSON.stringify(defaultValue)}"`,
    );

    console.error(validationResult.error);
    return setupLocalStorageRef(key, defaultValue);
  }
}
